import { useMemo, useState, type Dispatch, type Ref, type SetStateAction } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { ChatMessageList, ChatComposer, ChatThinkingIndicator } from "../aiChat/index.js";
import type { AiChatMessage } from "../../hooks/useAiChatThread.js";
import { TanTableViz } from "../TanTableViz.js";
import type { QueryResultsPayload } from "../QueryResultsGrid.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { previewHeaderLabel } from "../../lib/previewHeader.js";
import { ColumnDomainSelect, type ColumnDomain } from "../ColumnDomainControl.js";

const sqlExtensions = [sql({ dialect: PostgreSQL })];

export type TenantAiModelOption = { model_id: string; status: string };

/**
 * ADR-0129 — the transform workbench renders one spine section at a time so the
 * editor+run controls (Setup), the copilot chat (AI), and the result preview
 * (Data) can live in separate slots instead of one stacked panel. Shared props
 * flow to all three; `section` selects which block renders.
 */
export type TransformWorkbenchSection = "editor" | "chat" | "preview";

export type TransformInspectorWorkbenchProps = {
  section: TransformWorkbenchSection;
  operationStatus: "draft" | "invalid" | "published" | "paused" | "validate";
  transformForceRefresh: boolean;
  setTransformForceRefresh: Dispatch<SetStateAction<boolean>>;
  onRunTransformPreview: () => void;
  transformRunBusy: boolean;
  transformSending: boolean;
  onResetTransformChat: () => void;
  transformRunError: string | null;
  transformInteractionMode: "ask" | "agent";
  setTransformInteractionMode: Dispatch<SetStateAction<"ask" | "agent">>;
  tenantAiModels: TenantAiModelOption[];
  selectedAiModelId: string;
  onTenantAiModelChange: (modelId: string) => void;
  aiChatBlocked: boolean;
  transformChatScrollRef: Ref<HTMLDivElement>;
  transformMessages: Array<{ id: string; role: "user" | "assistant"; text: string }>;
  transformPrompt: string;
  setTransformPrompt: Dispatch<SetStateAction<string>>;
  onSendTransformPrompt: (message: string, onRestore: (msg: string) => void) => void;
  onCancelTransformChat: () => void;
  transformSql: string;
  onTransformSqlChange: (value: string) => void;
  transformJson: string;
  onTransformJsonChange: (value: string) => void;
  onTransformSpecBlur: () => void;
  transformJsonError: string | null;
  transformPreview: QueryResultsPayload | null;
  transformStats: { runAt: string | null; durationMs: number | null; rowCount: number | null } | null;
  transformSubTab: "data" | "schema";
  setTransformSubTab: Dispatch<SetStateAction<"data" | "schema">>;
  nodeCacheLoadingThisNode: boolean;
  nodeOutputRunAt: string | null | undefined;
  /** ADR-0130: input side of the schema mapping — each upstream node's columns.
   *  Rendered alongside the output columns in the Data tab's Schema subtab. */
  inputSchemas?: Array<{ nodeId: string; label: string; schema: Array<{ name: string; type: string }> | null }>;
  /** ADR-0147: current per-column Domain override map from node config. */
  columnOverrides?: Record<string, string>;
  /** ADR-0147: Domain change → autosave + in-place retype (wired by OperationEditor). */
  onColumnDomainChange?: (columnName: string, domain: ColumnDomain | null) => void;
  /** ADR-0147: a Domain change is saving/retyping — disables the selects. */
  retypeBusy?: boolean;
};

/** Shared transform run / chat / spec / preview UI, one spine section per render. */
export function TransformInspectorWorkbench(p: TransformInspectorWorkbenchProps) {
  const {
    section,
    operationStatus: status,
    transformForceRefresh,
    setTransformForceRefresh,
    onRunTransformPreview,
    transformRunBusy,
    transformSending,
    onResetTransformChat,
    transformRunError,
    transformInteractionMode,
    setTransformInteractionMode,
    tenantAiModels,
    selectedAiModelId,
    onTenantAiModelChange,
    aiChatBlocked,
    transformChatScrollRef,
    transformMessages,
    transformPrompt,
    setTransformPrompt,
    onSendTransformPrompt,
    onCancelTransformChat,
    transformSql,
    onTransformSqlChange,
    transformJson,
    onTransformJsonChange,
    onTransformSpecBlur,
    transformJsonError,
    transformPreview,
    transformStats,
    transformSubTab,
    setTransformSubTab,
    nodeCacheLoadingThisNode,
    nodeOutputRunAt,
    inputSchemas
  } = p;

  const [specTab, setSpecTab] = useState<"sql" | "json">("sql");
  // ADR-0130: per-upstream collapse state for the Schema subtab's input mapping.
  const [inputCollapsed, setInputCollapsed] = useState<Record<string, boolean>>({});

  const tanTableModel = useMemo(
    () => (transformPreview ? queryResultsToTanTableModel(transformPreview) : null),
    [transformPreview]
  );

  function handleSendTransformPrompt() {
    const message = transformPrompt.trim();
    if (!message) return;
    setTransformPrompt("");
    void onSendTransformPrompt(message, (restored) => setTransformPrompt(restored));
  }

  // ── AI slot: the "Ask Atlas (Transformation)" chat, frame-filling. ──────────
  if (section === "chat") {
    return (
      <div className="askChatPanel askChatPanelFill">
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexShrink: 0 }}>
          <select
            className={`pillSelect ${transformInteractionMode === "ask" ? "pillSelectNeutral" : "pillSelectOk"}`}
            value={transformInteractionMode}
            onChange={(e) => setTransformInteractionMode(e.target.value === "ask" ? "ask" : "agent")}
            disabled={transformSending || transformRunBusy || aiChatBlocked}
          >
            <option value="agent">Agent</option>
            <option value="ask">Ask</option>
          </select>
          {tenantAiModels.length > 0 ? (
            <select
              className="pillSelect pillSelectNeutral"
              value={selectedAiModelId}
              onChange={(e) => onTenantAiModelChange(e.target.value)}
              disabled={transformSending || transformRunBusy || aiChatBlocked}
              title="Model for this workspace"
            >
              {tenantAiModels.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_id}
                  {m.status === "deprecated" ? " (deprecated)" : ""}
                </option>
              ))}
            </select>
          ) : null}
          <div style={{ flex: 1 }} />
          <button
            className="btn btnSm"
            type="button"
            onClick={() => onResetTransformChat()}
            disabled={transformSending || transformRunBusy || aiChatBlocked}
          >
            Reset chat
          </button>
        </div>
        <div className="askChatMessages" ref={transformChatScrollRef}>
          <ChatMessageList<AiChatMessage>
            messages={transformMessages.map((msg) => ({
              id: msg.id,
              role: msg.role,
              content: msg.text,
              created_at: ""
            }))}
            assistantLabel="Atlas Transform agent"
            emptyState={
              <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>
                Describe the transformation you want — e.g. "Group by month and sum revenue."
              </div>
            }
            trailingNode={<ChatThinkingIndicator visible={transformSending} />}
          />
        </div>
        <ChatComposer
          draft={transformPrompt}
          setDraft={setTransformPrompt}
          onSend={handleSendTransformPrompt}
          onCancel={onCancelTransformChat}
          inFlight={transformSending}
          placeholder={transformInteractionMode === "ask" ? "Ask a question…" : "Describe the transformation…"}
          disabledExtra={transformRunBusy || aiChatBlocked}
          disabledExtraTitle={aiChatBlocked ? "AI chat temporarily limited — try again in about a minute." : undefined}
          minRows={2}
          maxRows={12}
        />
      </div>
    );
  }

  // ── Data slot: the run-result preview (stats + Data/Schema subtab). ─────────
  // ADR-0130: the Schema subtab is the Input → Output schema mapping (moved here
  // from the Setup editor). It's reachable even before a run so long as an
  // upstream schema exists — so you can inspect incoming columns while authoring.
  if (section === "preview") {
    const hasInputs = (inputSchemas?.length ?? 0) > 0;
    if (!transformPreview && !hasInputs) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {nodeCacheLoadingThisNode || transformRunBusy ? (
            <div className="muted">{transformRunBusy ? "Running transform…" : "Loading cached data…"}</div>
          ) : (
            <div className="muted">
              {nodeOutputRunAt
                ? "No table preview in cache (or it could not be loaded). Click Run to refresh."
                : "Click Run to execute the transform and see results."}
            </div>
          )}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {transformStats && (
          <div className="toolbarGroup" style={{ flexWrap: "wrap", gap: 4 }}>
            {transformStats.runAt ? <span className="pill">Last run: {new Date(transformStats.runAt).toLocaleString()}</span> : null}
            {transformStats.durationMs != null ? <span className="pill">Duration: {transformStats.durationMs} ms</span> : null}
            {transformStats.rowCount != null ? <span className="pill">Rows: {transformStats.rowCount.toLocaleString()}</span> : null}
          </div>
        )}
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--line)" }}>
          <button
            type="button"
            className={`reportTabBtn${transformSubTab === "data" ? " reportTabBtnActive" : ""}`}
            onClick={() => setTransformSubTab("data")}
          >
            Data
          </button>
          <button
            type="button"
            className={`reportTabBtn${transformSubTab === "schema" ? " reportTabBtnActive" : ""}`}
            onClick={() => setTransformSubTab("schema")}
          >
            Schema
          </button>
        </div>
        {transformSubTab === "data" ? (
          transformPreview && tanTableModel ? (
            <>
              {previewHeaderLabel(transformPreview) ? (
                <div className="muted" style={{ paddingBottom: 6 }}>{previewHeaderLabel(transformPreview)}</div>
              ) : null}
              <TanTableViz model={tanTableModel} />
            </>
          ) : (
            <div className="muted" style={{ fontSize: 12 }}>Run this transform to see its result data.</div>
          )
        ) : (
          <div className="schemaMappingLayout">
            <div className="schemaMappingPanel">
              <div className="schemaMappingPanelHeader">Input</div>
              {!hasInputs ? (
                <div className="muted" style={{ fontSize: 12 }}>No upstream connections yet.</div>
              ) : (
                inputSchemas!.map((inp) => {
                  const collapsed = inputCollapsed[inp.nodeId] ?? false;
                  return (
                    <div key={inp.nodeId} style={{ marginBottom: 10 }}>
                      <div
                        className="schemaMappingNodeLabel"
                        style={{ cursor: "pointer", userSelect: "none", fontSize: 12, lineHeight: 1.3, marginBottom: 4 }}
                        onClick={() => setInputCollapsed((prev) => ({ ...prev, [inp.nodeId]: !prev[inp.nodeId] }))}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <span style={{ display: "inline-block", width: 12, fontSize: 10, textAlign: "center", opacity: 0.6 }}>
                            {collapsed ? "▶" : "▼"}
                          </span>
                          {inp.label}
                        </span>
                        <span className="pill" style={{ fontSize: 9, padding: "1px 6px" }}>
                          {inp.schema ? `${inp.schema.length} col${inp.schema.length !== 1 ? "s" : ""}` : "—"}
                        </span>
                      </div>
                      {!collapsed && inp.schema && inp.schema.length > 0 ? (
                        <div className="tableWrap" style={{ marginTop: 4 }}>
                          <table className="table tableCompact">
                            <thead>
                              <tr><th>Column</th><th>Type</th></tr>
                            </thead>
                            <tbody>
                              {inp.schema.map((col) => (
                                <tr key={col.name}>
                                  <td style={{ wordBreak: "break-word" }}>{col.name}</td>
                                  <td className="muted">{col.type}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : !collapsed ? (
                        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Run this node to see its schema.</div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
            <div className="schemaMappingPanel">
              <div className="schemaMappingPanelHeader">Output</div>
              {transformPreview && transformPreview.columns.length > 0 ? (
                <>
                  <div className="tableWrap" style={{ marginTop: 4 }}>
                    <table className="table tableCompact">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Type</th>
                          {p.onColumnDomainChange ? <th>Domain</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {transformPreview.columns.map((col) => (
                          <tr key={col.name}>
                            <td style={{ wordBreak: "break-word" }}>{col.name}</td>
                            <td className="muted">{col.rawType ?? col.type}</td>
                            {p.onColumnDomainChange ? (
                              <td>
                                <ColumnDomainSelect
                                  columnName={col.name}
                                  resolvedDomain={col.type}
                                  override={p.columnOverrides?.[col.name]}
                                  disabled={p.retypeBusy}
                                  onChange={p.onColumnDomainChange}
                                />
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Run this transform to see its output schema.</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Setup slot: run controls + transformation spec editor. ──────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="toolbarGroup" style={{ flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={transformForceRefresh}
            onChange={(e) => setTransformForceRefresh(e.target.checked)}
          />
          Full refresh
        </label>
        <button
          className="btn btnSm"
          type="button"
          onClick={() => void onRunTransformPreview()}
          disabled={transformRunBusy || transformSending || status === "validate"}
          title={
            status === "validate"
              ? "Operation requires validation"
              : transformForceRefresh
                ? "Re-run provider + transform"
                : "Run transform (uses provider cache if available)"
          }
        >
          {transformRunBusy ? "Running…" : "Run"}
        </button>
      </div>

      {transformRunError ? <div className="errorState">{transformRunError}</div> : null}

      <div className="askConfig">
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--line)", marginBottom: 8 }}>
          <button
            type="button"
            className={`reportTabBtn${specTab === "sql" ? " reportTabBtnActive" : ""}`}
            onClick={() => setSpecTab("sql")}
          >
            SQL
          </button>
          <button
            type="button"
            className={`reportTabBtn${specTab === "json" ? " reportTabBtnActive" : ""}`}
            onClick={() => setSpecTab("json")}
          >
            JSON
          </button>
        </div>
        {specTab === "sql" ? (
          <div className="queryEditorCm">
            <CodeMirror
              value={transformSql}
              height="240px"
              extensions={sqlExtensions}
              placeholder="SELECT ... FROM source"
              onChange={(val) => onTransformSqlChange(val)}
              basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true, bracketMatching: true, autocompletion: false }}
            />
          </div>
        ) : (
          <>
            <textarea
              className="input reportJsonEditor"
              value={transformJson}
              onChange={(e) => onTransformJsonChange(e.target.value)}
              onBlur={() => {
                onTransformSpecBlur();
              }}
              spellCheck={false}
              rows={14}
            />
            {transformJsonError ? <div className="errorState">{transformJsonError}</div> : null}
          </>
        )}
        <div className="toolbarGroup" style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <button
            className="btn btnSm"
            type="button"
            onClick={() => void onRunTransformPreview()}
            disabled={transformRunBusy || transformSending || !!transformJsonError || status === "validate"}
          >
            Run Transformation
          </button>
        </div>
      </div>
    </div>
  );
}
